import { AnyAction } from 'redux';
import { ServerResponse } from 'data-layer-cr/dist/models';
import { setRequestStatus } from 'data-layer-cr/dist/network';
import processRequest from 'src/services/processRequest';
import { makeYappyPaymentPayload } from 'src/models/requestTypes/creators/makeEmissionRequest';
import {
  SignatureCretationRequest,
  PinGenerationRequest,
  PinValidationRequest,
} from 'src/models/requestTypes/SignatureRequest';
import {
  SignatureDataResponse,
  PinValidationResponse,
} from 'src/models/responseTypes/SignatureResponse';
import { AppThunk, AsyncAppThunk, AsyncDispatch } from '../../ThunkTypes';
import {
  REQUEST_CREATE_SIGNATURE,
  REQUEST_CREATE_PIN,
  REQUEST_VALIDATE_PIN,
} from '../requestIdentifiers';
import {
  setFileUrl,
  setOrderId,
  setErrorRequest,
  setPinRequested,
  setValidationPinError,
  setValidationPinCode,
  setSignatureCompleted,
  setAlertType,
  setShowAlerts,
} from '../slice/signatureSlice';
import {
  getUserSocialReason,
  getUserAgentBrowser,
  getUserOS,
} from './helperFuncions';

export const SignatureAlerts = {
  PIN_REQUEST_SUCCESS: 'PIN_REQUEST_SUCCESS',
  PIN_REQUEST_ERROR: 'PIN_REQUEST_ERROR',
  PIN_VALIDATION_ERROR: 'PIN_VALIDATION_ERROR',
};

export const requestSignatureCreation =
  (): AsyncAppThunk =>
  async (dispatch: AsyncDispatch, getState): Promise<AnyAction> => {
    // current state
    dispatch(setRequestStatus(REQUEST_CREATE_SIGNATURE, 'requesting'));
    const currentState = getState();
    const { emissionData } = makeYappyPaymentPayload(currentState);
    const { salesEmissionRequest } = emissionData;

    const signatureData: SignatureCretationRequest = {
      email: salesEmissionRequest.client.email,
      explorer: getUserAgentBrowser(),
      firstName: salesEmissionRequest.client.firstName,
      lastName: salesEmissionRequest.client.lastName,
      phone: salesEmissionRequest.client.phone1,
      quotationHash: emissionData.urlSalt,
      socialReason: getUserSocialReason(salesEmissionRequest.client),
      signerLegalId: salesEmissionRequest.client.id,
    };

    // Perform the network request
    try {
      const signInfo = await processRequest<
        ServerResponse<SignatureDataResponse>
      >({
        method: 'POST',
        url: 'PolicyEmission/signature',
        data: signatureData,
      });

      dispatch(setOrderId(signInfo.data.id));
      dispatch(setFileUrl(signInfo.data.documentURL));
      dispatch(setErrorRequest(false));
      return dispatch(setRequestStatus(REQUEST_CREATE_SIGNATURE, 'success'));
    } catch (e) {
      dispatch(setErrorRequest(true));
      return dispatch(setRequestStatus(REQUEST_CREATE_SIGNATURE, 'failure'));
    }
  };

export const requestPinCreation =
  (): AsyncAppThunk =>
  async (dispatch: AsyncDispatch, getState): Promise<AnyAction> => {
    // current state
    dispatch(setRequestStatus(REQUEST_CREATE_PIN, 'requesting'));
    const currentState = getState();
    const {
      emissionData: { salesEmissionRequest },
    } = makeYappyPaymentPayload(currentState);

    const pinData: PinGenerationRequest = {
      email: salesEmissionRequest.client.email,
      name: salesEmissionRequest.client.firstName,
      lastName: salesEmissionRequest.client.lastName,
      singId: currentState.signature.orderId,
    };

    // Perform the network request
    try {
      await processRequest<unknown>({
        method: 'POST',
        url: 'PolicyEmission/pin',
        data: pinData,
      });

      dispatch(setPinRequested(true));
      dispatch(setAlertType(SignatureAlerts.PIN_REQUEST_SUCCESS));
      dispatch(setShowAlerts(true));
      return dispatch(setRequestStatus(REQUEST_CREATE_PIN, 'success'));
    } catch (e) {
      dispatch(setPinRequested(true));
      dispatch(setAlertType(SignatureAlerts.PIN_REQUEST_ERROR));
      dispatch(setShowAlerts(true));
      return dispatch(setRequestStatus(REQUEST_CREATE_PIN, 'failure'));
    }
  };

export const requestPinValidation =
  (token: string): AsyncAppThunk =>
  async (dispatch: AsyncDispatch, getState): Promise<AnyAction> => {
    // current state
    dispatch(setRequestStatus(REQUEST_VALIDATE_PIN, 'requesting'));
    const currentState = getState();

    const validationData: PinValidationRequest = {
      explorer: getUserAgentBrowser(),
      ip: '',
      signerOS: getUserOS(),
      signId: currentState.signature.orderId,
      token,
    };

    // Perform the network request
    try {
      const validationResponse = await processRequest<
        ServerResponse<PinValidationResponse>
      >({
        method: 'POST',
        url: 'PolicyEmission/documentsigning',
        data: validationData,
      });

      dispatch(setValidationPinError(validationResponse.data.isError));
      dispatch(setValidationPinCode(validationResponse.data.code));
      if (!validationResponse.data.isError) {
        dispatch(setSignatureCompleted(true));
      }
      return dispatch(setRequestStatus(REQUEST_VALIDATE_PIN, 'success'));
    } catch (e) {
      dispatch(setAlertType(SignatureAlerts.PIN_VALIDATION_ERROR));
      dispatch(setShowAlerts(true));
      return dispatch(setRequestStatus(REQUEST_VALIDATE_PIN, 'failure'));
    }
  };

export const resetSignatureStatus =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(setOrderId(''));
    dispatch(setFileUrl(''));
    dispatch(setErrorRequest(false));
    dispatch(setPinRequested(false));
    dispatch(setShowAlerts(false));
    dispatch(setAlertType(''));
    dispatch(setValidationPinError(false));
    dispatch(setValidationPinCode(''));
    dispatch(setSignatureCompleted(false));
  };

export const closeSignatureAlerts =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(setAlertType(''));
    dispatch(setShowAlerts(false));
  };

export const cleanValidationErrors =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(setValidationPinError(false));
    dispatch(setValidationPinCode(''));
  };
